import { ArrowLeft } from "lucide-react";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";

import {
	activateStaffById,
	deactivateStaffById,
	getUserById,
	getVisits,
	resendInvitationById,
	sendPasswordResetById,
} from "../config/url";
import { getConfigWithToken } from "../config/config";
import { getDisplayRole, getStaffRoles, hasProviderAccess, resolveStaffStatus } from "../utils/staffRoles";
import classes from "./StaffDetails.module.css";
import EditStaffDrawer from "../components/EditStaffDrawer";
import formatPhoneNumber from "../utils/formatPhoneNumber";

const ROLE_CLASS = {
	Admin: classes.roleAdmin,
	Provider: classes.roleStaff,
	QA: classes.roleCompliance,
	Supervisor: classes.roleSupervisor,
};

const STATUS_CLASS = {
	Active: classes.statusActive,
	Inactive: classes.statusInactive,
	Pending: classes.statusPending,
};

const ALL_TABS = [
	{ key: "profile", label: "Profile" },
	{ key: "clients", label: "Assigned Clients", providerOnly: true },
	{ key: "visits", label: "Visits" },
	{ key: "forms", label: "Encounter Forms" },
];

const formatDateValue = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

const formatPhoneValue = (value) => {
	if (!value) return "-";
	try {
		return formatPhoneNumber(String(value)) || value;
	} catch {
		return value;
	}
};

const formatTimeValue = (value) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
};

const getClientName = (client) => [client?.firstName, client?.lastName].filter(Boolean).join(" ").trim() || "-";

const getDisplayName = (staffItem) =>
	[staffItem?.firstname, staffItem?.lastname].filter(Boolean).join(" ").trim() || staffItem?.email || "-";

const isActiveClient = (status) => {
	if (typeof status === "boolean") return status;
	if (typeof status === "number") return status === 1;
	if (typeof status === "string") {
		const normalized = status.trim().toLowerCase();
		return normalized === "active" || normalized === "true" || normalized === "1";
	}
	return false;
};

const getClientStatusLabel = (status) => (isActiveClient(status) ? "Active" : "Inactive");

const isVerifiedVisit = (status) => {
	if (status == null) return false;
	if (typeof status === "boolean") return status;
	if (typeof status === "number") return status === 1;
	if (typeof status === "string") {
		const normalized = status.trim().toLowerCase();
		return normalized === "verified" || normalized === "active" || normalized === "true" || normalized === "1";
	}
	return false;
};

const StaffDetails = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId ||
		localStorage.getItem("organizationId") ||
		localStorage.getItem("businessId");

	const [activeTab, setActiveTab] = useState("profile");
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [isEditOpen, setIsEditOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isStatusUpdating, setIsStatusUpdating] = useState(false);
	const [isResendingInvitation, setIsResendingInvitation] = useState(false);
	const [isVisitsLoading, setIsVisitsLoading] = useState(false);
	const [assignedClients, setAssignedClients] = useState([]);
	const [assignedClientsError, setAssignedClientsError] = useState(false);
	const [assignedClientsTotal, setAssignedClientsTotal] = useState(0);
	const [isClientsLoading, setIsClientsLoading] = useState(false);
	const [staff, setStaff] = useState(null);
	const [visits, setVisits] = useState([]);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
			params: organizationId ? { organizationId } : {},
		}),
		[organizationId, token],
	);

	const loadStaff = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axios.get(getUserById(id), requestConfig);
			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to load staff.");
				setStaff(null);
				setAssignedClients([]);
				setAssignedClientsTotal(0);
				setAssignedClientsError(false);
				return;
			}
			setStaff(response.data?.data || response.data);
			const staffData = response.data?.data || response.data;
			const rows = staffData?.assignedClients;
			const total = staffData?.assigned_clients ?? (Array.isArray(rows) ? rows.length : 0);
			setAssignedClients(Array.isArray(rows) ? rows : []);
			setAssignedClientsTotal(total);
			setAssignedClientsError(false);
		} catch (error) {
			const status = error?.response?.status;
			toast.error(error?.response?.data?.message || "Failed to load staff.");
			setStaff(null);
			setAssignedClients([]);
			setAssignedClientsTotal(0);
			setAssignedClientsError(false);
			if (status === 403 || status === 404) {
				navigate("/staff", { replace: true });
			}
		} finally {
			setIsLoading(false);
		}
	}, [id, navigate, requestConfig]);

	const loadAssignedClients = useCallback(async () => {
		if (!id) return;
		setIsClientsLoading(true);
		setAssignedClientsError(false);
		try {
			const response = await axios.get(getUserById(id), requestConfig);
			if (response.data?.success === false) {
				setAssignedClients([]);
				setAssignedClientsTotal(0);
				setAssignedClientsError(true);
				toast.error(response.data?.message || "Failed to load assigned clients.");
				return;
			}
			const staffData = response.data?.data || response.data;
			const rows = staffData?.assignedClients;
			const total = staffData?.assigned_clients ?? (Array.isArray(rows) ? rows.length : 0);
			setAssignedClients(Array.isArray(rows) ? rows : []);
			setAssignedClientsTotal(total);
			setStaff((previous) => (previous ? { ...previous, ...staffData } : staffData));
		} catch (error) {
			setAssignedClients([]);
			setAssignedClientsTotal(0);
			setAssignedClientsError(true);
			toast.error(error.response?.data?.message || "Failed to load assigned clients.");
		} finally {
			setIsClientsLoading(false);
		}
	}, [id, requestConfig]);

	const loadVisits = useCallback(async () => {
		if (!id) return;
		setIsVisitsLoading(true);
		try {
			const response = await axios.get(getVisits, {
				...requestConfig,
				params: {
					...(requestConfig.params || {}),
					limit: 100,
					page: 1,
					userId: id,
				},
			});
			const rows = response?.data?.data;
			setVisits(Array.isArray(rows) ? rows : []);
		} catch {
			setVisits([]);
		} finally {
			setIsVisitsLoading(false);
		}
	}, [id, requestConfig]);

	useEffect(() => {
		loadStaff();
	}, [loadStaff]);

	const accountStatus = staff ? staff.accountStatus || resolveStaffStatus(staff) : null;
	const displayName = getDisplayName(staff);
	const displayRole = getDisplayRole(staff);
	const staffRoles = getStaffRoles(staff);
	const isProvider = staff?.isProvider === true || hasProviderAccess(staff);
	const tabClientCount =
		activeTab === "clients" && !isClientsLoading && !assignedClientsError
			? assignedClientsTotal
			: staff?.assigned_clients ?? assignedClientsTotal ?? 0;
	const visibleTabs = useMemo(
		() => ALL_TABS.filter((tab) => !tab.providerOnly || isProvider),
		[isProvider],
	);
	const npiValue = staff?.providerInformation?.npi || staff?.npi || "-";

	useEffect(() => {
		if (activeTab === "visits") loadVisits();
	}, [activeTab, loadVisits]);

	useEffect(() => {
		if (activeTab === "clients" && isProvider) loadAssignedClients();
	}, [activeTab, isProvider, loadAssignedClients]);

	useEffect(() => {
		if (!isProvider && activeTab === "clients") {
			setActiveTab("profile");
		}
	}, [activeTab, isProvider]);

	const handleDeactivate = async () => {
		setIsStatusUpdating(true);
		try {
			const response = await axios.put(deactivateStaffById(id), {}, requestConfig);
			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to deactivate staff.");
				return;
			}
			toast.success(response.data?.message || "Staff deactivated.");
			setIsConfirmOpen(false);
			await loadStaff();
		} catch (error) {
			toast.error(error?.response?.data?.message || "Failed to deactivate staff.");
		} finally {
			setIsStatusUpdating(false);
		}
	};

	const handleReactivate = async () => {
		setIsStatusUpdating(true);
		try {
			const response = await axios.put(activateStaffById(id), {}, requestConfig);
			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to reactivate staff.");
				return;
			}
			toast.success(response.data?.message || "Staff reactivated.");
			await loadStaff();
		} catch (error) {
			toast.error(error?.response?.data?.message || "Failed to reactivate staff.");
		} finally {
			setIsStatusUpdating(false);
		}
	};

	const handleSendPasswordReset = async () => {
		try {
			const response = await axios.post(sendPasswordResetById(id), {}, requestConfig);
			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to send reset email.");
				return;
			}
			toast.success(response.data?.message || "Password reset email sent.");
		} catch (error) {
			toast.error(error?.response?.data?.message || "Failed to send reset email.");
		}
	};

	const handleResendActivationLink = async () => {
		setIsResendingInvitation(true);
		try {
			const response = await axios.post(resendInvitationById(id), {}, requestConfig);
			if (response.data?.success === false) {
				toast.error(response.data?.message || "Failed to resend activation link.");
				return;
			}
			toast.success(response.data?.message || "Activation link sent.");
		} catch (error) {
			toast.error(error?.response?.data?.message || "Failed to resend activation link.");
		} finally {
			setIsResendingInvitation(false);
		}
	};

	if (isLoading) {
		return (
			<div className={classes.page}>
				<div className={classes.stateBlock}>
					<p className={classes.stateText}>Loading staff profile...</p>
				</div>
			</div>
		);
	}

	if (!staff) {
		return (
			<div className={classes.page}>
				<div className={classes.stateBlock}>
					<p className={classes.stateTitle}>Staff not found</p>
					<p className={classes.stateText}>This staff member could not be loaded.</p>
					<button className={classes.secondaryButton} onClick={() => navigate("/staff")} type="button">
						Back to Staff
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className={classes.page}>
			<button className={classes.backRow} onClick={() => navigate("/staff")} type="button">
				<span className={classes.backIcon}>
					<ArrowLeft size={18} strokeWidth={2.25} />
				</span>
				<span className={classes.backLabel}>Staff Profile</span>
			</button>

			<div className={classes.headerCard}>
				<div className={classes.headerMain}>
					<div className={classes.nameRow}>
						<h1 className={classes.name}>{displayName}</h1>
						<span className={`${classes.statusBadge} ${STATUS_CLASS[accountStatus] || ""}`}>{accountStatus}</span>
					</div>
					<p className={classes.staffId}>Staff ID: {staff.userId || "-"}</p>
				</div>
				<div className={classes.headerActions}>
					{accountStatus === "Active" ? (
						<button
							className={classes.deactivateButton}
							disabled={isStatusUpdating}
							onClick={() => setIsConfirmOpen(true)}
							type="button">
							Deactivate
						</button>
					) : null}
					{accountStatus === "Inactive" ? (
						<button
							className={classes.reactivateButton}
							disabled={isStatusUpdating}
							onClick={handleReactivate}
							type="button">
							{isStatusUpdating ? "Reactivating..." : "Reactivate"}
						</button>
					) : null}
					<button className={classes.editButton} onClick={() => setIsEditOpen(true)} type="button">
						Edit
					</button>
				</div>
			</div>

			<div className={classes.tabs} role="tablist">
				{visibleTabs.map((tab) => {
					const label = tab.key === "clients" ? `${tab.label} (${tabClientCount})` : tab.label;
					return (
						<button
							aria-selected={activeTab === tab.key}
							className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
							key={tab.key}
							onClick={() => setActiveTab(tab.key)}
							role="tab"
							type="button">
							{label}
						</button>
					);
				})}
			</div>

			{activeTab === "profile" ? (
				<div className={classes.profileContent}>
					<section className={classes.sectionCard}>
						<div className={classes.sectionHeader}>Staff Info</div>
						<div className={classes.infoGrid}>
							<div className={classes.infoItem}>
								<span className={classes.infoLabel}>First Name</span>
								<span className={classes.infoValue}>{staff.firstname || "-"}</span>
							</div>
							<div className={classes.infoItem}>
								<span className={classes.infoLabel}>Last Name</span>
								<span className={classes.infoValue}>{staff.lastname || "-"}</span>
							</div>
							<div className={classes.infoItem}>
								<span className={classes.infoLabel}>Email</span>
								<span className={classes.infoValue}>{staff.email || "-"}</span>
							</div>
							<div className={classes.infoItem}>
								<span className={classes.infoLabel}>Roles</span>
								<div className={classes.roleList}>
									{staffRoles.length ? (
										staffRoles.map((role) => (
											<span
												className={`${classes.roleBadge} ${ROLE_CLASS[role] || classes.roleDefault}`}
												key={role}>
												{role}
											</span>
										))
									) : displayRole ? (
										<span
											className={`${classes.roleBadge} ${ROLE_CLASS[displayRole] || classes.roleDefault}`}>
											{displayRole}
										</span>
									) : (
										<span className={classes.infoValue}>-</span>
									)}
								</div>
							</div>
							<div className={classes.infoItem}>
								<span className={classes.infoLabel}>NPI</span>
								<span className={classes.infoValue}>{isProvider ? npiValue : "-"}</span>
							</div>
							<div className={classes.infoItemEmpty} />
						</div>
					</section>

					<section className={classes.sectionCard}>
						<div className={`${classes.sectionHeader} ${classes.sectionHeaderAccount}`}>Account</div>
						{accountStatus === "Active" ? (
							<div className={classes.accountActionsStandalone}>
								<div className={classes.actionRow}>
									<div>
										<p className={classes.actionTitle}>Reset password</p>
										<p className={classes.actionSubtitle}>Send a password reset email.</p>
									</div>
									<button className={classes.editButton} onClick={handleSendPasswordReset} type="button">
										Send Reset Email
									</button>
								</div>
							</div>
						) : null}
						{accountStatus === "Pending" ? (
							<div className={classes.accountActionsStandalone}>
								<div className={classes.actionRow}>
									<div>
										<p className={classes.actionTitle}>Resend activation link</p>
										<p className={classes.actionSubtitle}>Send a new account activation email.</p>
									</div>
									<button
										className={classes.editButton}
										disabled={isResendingInvitation}
										onClick={handleResendActivationLink}
										type="button">
										{isResendingInvitation ? "Sending..." : "Resend Activation Link"}
									</button>
								</div>
							</div>
						) : null}
					</section>
				</div>
			) : null}

			{activeTab === "clients" && isProvider ? (
				<section className={classes.sectionCard}>
					<div className={classes.sectionHeader}>Assigned Clients ({tabClientCount})</div>
					{isClientsLoading ? (
						<div className={classes.placeholderBody}>
							<p className={classes.stateText}>Loading assigned clients...</p>
						</div>
					) : assignedClientsError ? (
						<div className={classes.placeholderBody}>
							<p className={classes.stateTitle}>Unable to load assigned clients</p>
							<p className={classes.stateText}>Please try again.</p>
							<button className={classes.secondaryButton} onClick={loadAssignedClients} type="button">
								Retry
							</button>
						</div>
					) : assignedClients.length === 0 ? (
						<div className={classes.placeholderBody}>
							<p className={classes.stateTitle}>No clients assigned</p>
							<p className={classes.stateText}>This provider does not currently have any assigned clients.</p>
						</div>
					) : (
						<div className={classes.tableWrap}>
							<table className={classes.dataTable}>
								<thead>
									<tr>
										<th>Client Name</th>
										<th>Client ID</th>
										<th>DOB</th>
										<th>Phone</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{assignedClients.map((client) => {
										const clientActive = isActiveClient(client.status);
										const clientRouteId = client.id;
										return (
											<tr
												className={classes.clientRow}
												key={client.id || client.clientid}
												onClick={() => clientRouteId && navigate(`/clients/${clientRouteId}`)}
												onKeyDown={(event) => {
													if ((event.key === "Enter" || event.key === " ") && clientRouteId) {
														event.preventDefault();
														navigate(`/clients/${clientRouteId}`);
													}
												}}
												role="link"
												tabIndex={0}>
												<td className={classes.nameCell}>{getClientName(client)}</td>
												<td>
													<span className={classes.linkText}>{client.clientid || "-"}</span>
												</td>
												<td>{formatDateValue(client.dob)}</td>
												<td>{formatPhoneValue(client.phone_number)}</td>
												<td>
													<span
														className={`${classes.statusBadge} ${
															clientActive ? classes.statusActive : classes.statusInactiveMuted
														}`}>
														{getClientStatusLabel(client.status)}
													</span>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</section>
			) : null}

			{activeTab === "visits" ? (
				<section className={classes.sectionCard}>
					<div className={classes.sectionHeader}>Visits ({visits.length})</div>
					{isVisitsLoading ? (
						<div className={classes.placeholderBody}>
							<p className={classes.stateText}>Loading visits...</p>
						</div>
					) : visits.length === 0 ? (
						<div className={classes.placeholderBody}>
							<p className={classes.stateText}>No visits found for this staff member.</p>
						</div>
					) : (
						<div className={classes.tableWrap}>
							<table className={classes.dataTable}>
								<thead>
									<tr>
										<th>Visit ID</th>
										<th>Client</th>
										<th>Date</th>
										<th>Time In</th>
										<th>Time Out</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{visits.map((visit) => {
										const verified = isVerifiedVisit(visit.verification_status);
										const visitId = visit.id || visit._id;
										return (
											<tr key={visitId}>
												<td>
													<button
														className={classes.linkButton}
														onClick={() => navigate(`/visits/${visitId}`, { state: { visit } })}
														type="button">
														{visit.visitid || visitId || "-"}
													</button>
												</td>
												<td className={classes.nameCell}>
													{[visit.client?.firstName, visit.client?.lastName]
														.filter(Boolean)
														.join(" ") ||
														visit.client?.clientid ||
														"-"}
												</td>
												<td>{formatDateValue(visit.date || visit.created_at)}</td>
												<td>{formatTimeValue(visit.time_in || visit.timeIn)}</td>
												<td>{formatTimeValue(visit.time_out || visit.timeOut)}</td>
												<td>
													{visit.verification_status ? (
														<span
															className={`${classes.statusBadge} ${
																verified ? classes.statusActive : classes.statusFlagged
															}`}>
															{verified ? "Verified" : "Flagged"}
														</span>
													) : (
														"-"
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</section>
			) : null}

			{activeTab === "forms" ? (
				<section className={classes.sectionCard}>
					<div className={classes.sectionHeader}>Encounter Forms</div>
					<div className={classes.placeholderBody}>
						<p className={classes.stateText}>Encounter forms for this staff member will appear here.</p>
					</div>
				</section>
			) : null}

			{isConfirmOpen ? (
				<div className={classes.modalOverlay}>
					<div className={classes.modalCard} role="dialog">
						<p className={classes.modalText}>Are you sure you want to deactivate {displayName}?</p>
						<button
							className={classes.confirmButton}
							disabled={isStatusUpdating}
							onClick={handleDeactivate}
							type="button">
							{isStatusUpdating ? "Deactivating..." : "Yes, deactivate"}
						</button>
						<button
							className={classes.cancelModalButton}
							disabled={isStatusUpdating}
							onClick={() => setIsConfirmOpen(false)}
							type="button">
							No, keep active
						</button>
					</div>
				</div>
			) : null}

			<EditStaffDrawer
				isOpen={isEditOpen}
				onClose={() => setIsEditOpen(false)}
				onSuccess={async () => {
					await loadStaff();
					if (activeTab === "clients") await loadAssignedClients();
				}}
				staff={staff}
			/>
		</div>
	);
};

export default StaffDetails;
